// Lock Feature utilities
export {
  findAvailableModules,
  isStepLocked,
  getLockMessage,
  type LockFeatureType,
  type LockFeatureExtra,
  type LockFeatureValidationParams
} from './lock-feature';

// Handshake utilities
export {
  generateHandshakeUrl,
  validateHandshakeParams,
  normalizeRedirectUrl
} from './handshake';

/**
 * Domínios padrão que não são white-label
 */
export const DEFAULT_DOMAINS = ['www', 'localhost'] as const
export const PLATFORM_BASE_DOMAINS = ['mentorfy.io', 'mentorfy.com'] as const

/**
 * Extrai o identificador de domínio para busca de tema white-label.
 * 
 * Lógica:
 * - Se o host não contém ponto, termina com .mentorfy.io ou .mentorfy.com → extrai o subdomínio (referrer)
 * - Se é um domínio personalizado (ex: omniflix.app.br) → retorna o domínio completo
 * 
 * @param hostname - O hostname completo (ex: "4wpro6.mentorfy.io" ou "omniflix.app.br")
 * @returns O identificador do domínio para buscar o tema, ou null se for domínio padrão
 */
export function getWhiteLabelDomain(hostname: string): string | null {
  if (!hostname) return null

  // Remove porta e prefixo www para evitar falso negativo em domínios customizados
  const host = hostname.split(':')[0].toLowerCase().replace(/^www\./, '')

  if (PLATFORM_BASE_DOMAINS.includes(host as typeof PLATFORM_BASE_DOMAINS[number])) {
    return null
  }

  // Verifica se é um domínio padrão
  const firstSegment = host.split('.')[0]
  if (DEFAULT_DOMAINS.includes(firstSegment as typeof DEFAULT_DOMAINS[number])) {
    return null
  }

  // Se não tem ponto, é apenas um referrer (ex: "4wpro6" em localhost)
  if (!host.includes('.')) {
    return host
  }

  // Se termina com .mentorfy.io, extrair apenas o subdomínio (referrer)
  if (host.endsWith('.mentorfy.io')) {
    return firstSegment
  }

  // Se termina com .mentorfy.com, extrair apenas o subdomínio (referrer)
  if (host.endsWith('.mentorfy.com')) {
    return firstSegment
  }

  // Se termina com .localhost (desenvolvimento), extrair o subdomínio
  if (host.endsWith('.localhost')) {
    return firstSegment
  }

  // Para qualquer outro domínio personalizado, retorna o domínio completo
  // Ex: "omniflix.app.br" → "omniflix.app.br"
  return host
}

/**
 * Verifica se o hostname atual é um domínio white-label
 */
export function isWhiteLabelDomain(hostname: string): boolean {
  return getWhiteLabelDomain(hostname) !== null
}

/**
 * Removes HTML tags from a string, returning only plain text
 * @param html - String that may contain HTML tags
 * @returns Plain text without HTML tags
 */
export function stripHtml(html: string | null | undefined): string {
  if (!html) return ''
  // Remove HTML tags and decode HTML entities
  return html
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
    .replace(/&amp;/g, '&') // Replace &amp; with &
    .replace(/&lt;/g, '<') // Replace &lt; with <
    .replace(/&gt;/g, '>') // Replace &gt; with >
    .replace(/&quot;/g, '"') // Replace &quot; with "
    .replace(/&#39;/g, "'") // Replace &#39; with '
    .replace(/&apos;/g, "'") // Replace &apos; with '
    .trim()
}
